import { useState, useEffect, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import type { CompanyDetail, CompanySignal, CompanyReport, CompanyResearchRun } from '../types'

// Types for progress tracking
interface ResearchProgress {
  run_id: number
  status: string
  progress_pct: number
  message: string
  tasks_completed: number
  tasks_total: number
  searches_completed: number
  error?: string
  done?: boolean
}

const API_BASE = '/api/v1'

// API Functions
async function fetchCompany(companyId: string): Promise<CompanyDetail> {
  const res = await fetch(`${API_BASE}/company-research/companies/${companyId}`)
  if (!res.ok) throw new Error('Failed to fetch company')
  return res.json()
}

async function fetchSignals(companyId: string, signalType?: string): Promise<CompanySignal[]> {
  const url = signalType
    ? `${API_BASE}/company-research/companies/${companyId}/signals?signal_type=${signalType}`
    : `${API_BASE}/company-research/companies/${companyId}/signals`
  const res = await fetch(url)
  if (!res.ok) throw new Error('Failed to fetch signals')
  return res.json()
}

async function fetchLatestReport(companyId: string): Promise<CompanyReport | null> {
  const res = await fetch(`${API_BASE}/company-research/companies/${companyId}/latest-report`)
  if (res.status === 404) return null
  if (!res.ok) throw new Error('Failed to fetch report')
  return res.json()
}

async function fetchRuns(companyId: string): Promise<CompanyResearchRun[]> {
  const res = await fetch(`${API_BASE}/company-research/companies/${companyId}/runs`)
  if (!res.ok) throw new Error('Failed to fetch runs')
  return res.json()
}

async function triggerResearch(companyId: number): Promise<{ id: number }> {
  const res = await fetch(`${API_BASE}/company-research/companies/${companyId}/research`, {
    method: 'POST',
  })
  if (!res.ok) throw new Error('Failed to trigger research')
  return res.json()
}

async function fetchProgress(runId: number): Promise<ResearchProgress> {
  const res = await fetch(`${API_BASE}/company-research/runs/${runId}/progress`)
  if (!res.ok) throw new Error('Failed to fetch progress')
  return res.json()
}

// Signal type config
const signalTypeConfig: Record<string, { color: string; icon: string }> = {
  hiring: { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: '👥' },
  product_launch: { color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: '🚀' },
  partnership: { color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: '🤝' },
  acquisition: { color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', icon: '💰' },
  risk_event: { color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: '⚠️' },
  catalyst: { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: '📅' },
  earnings: { color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30', icon: '📊' },
  competitor_move: { color: 'bg-pink-500/20 text-pink-400 border-pink-500/30', icon: '🎯' },
  news: { color: 'bg-gray-500/20 text-gray-400 border-gray-500/30', icon: '📰' },
  executive_change: { color: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30', icon: '👔' },
  regulatory: { color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: '📜' },
  guidance: { color: 'bg-teal-500/20 text-teal-400 border-teal-500/30', icon: '🔮' },
}

// Sentiment colors
const sentimentColors = {
  bullish: 'text-green-400',
  bearish: 'text-red-400',
  neutral: 'text-star-dim',
}

// Format date
function formatDate(dateStr?: string): string {
  if (!dateStr) return 'N/A'
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

// Scenario Card
function ScenarioCard({ name, scenario }: { name: string; scenario: { probability: number; drivers: string[]; target_outcome?: string; key_assumptions: string[] } }) {
  const colors = {
    bull: 'border-green-500/30 bg-green-500/5',
    base: 'border-blue-500/30 bg-blue-500/5',
    bear: 'border-red-500/30 bg-red-500/5',
  }

  const textColors = {
    bull: 'text-green-400',
    base: 'text-blue-400',
    bear: 'text-red-400',
  }

  return (
    <div className={`glass-card p-3 sm:p-4 rounded-lg border ${colors[name as keyof typeof colors]}`}>
      <div className="flex items-center justify-between mb-2 sm:mb-3">
        <h4 className={`text-sm sm:text-base font-semibold ${textColors[name as keyof typeof textColors]}`}>
          {name.charAt(0).toUpperCase() + name.slice(1)} Case
        </h4>
        <span className={`text-lg sm:text-xl font-bold ${textColors[name as keyof typeof textColors]}`}>
          {(scenario.probability * 100).toFixed(0)}%
        </span>
      </div>

      {scenario.target_outcome && (
        <p className="text-xs sm:text-sm text-star-bright mb-2 sm:mb-3">{scenario.target_outcome}</p>
      )}

      {scenario.drivers.length > 0 && (
        <div className="mb-2 sm:mb-3">
          <h5 className="text-[10px] sm:text-xs text-star-dim mb-1">Drivers</h5>
          <ul className="text-xs sm:text-sm text-star-bright space-y-1">
            {scenario.drivers.slice(0, 3).map((driver, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-star-dim shrink-0">•</span>
                <span>{driver}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {scenario.key_assumptions.length > 0 && (
        <div>
          <h5 className="text-[10px] sm:text-xs text-star-dim mb-1">Key Assumptions</h5>
          <ul className="text-[10px] sm:text-xs text-star-dim space-y-1">
            {scenario.key_assumptions.slice(0, 2).map((assumption, i) => (
              <li key={i}>• {assumption}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

// Signal Card
function SignalCard({ signal }: { signal: CompanySignal }) {
  const config = signalTypeConfig[signal.signal_type] || signalTypeConfig.news

  return (
    <div className={`glass-card p-4 rounded-lg border ${config.color}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <span className="text-xl">{config.icon}</span>
          <div>
            <h4 className="font-medium text-star-bright">{signal.title}</h4>
            <p className="text-sm text-star-dim mt-1 line-clamp-2">{signal.summary}</p>
          </div>
        </div>
        {signal.sentiment && (
          <span className={`text-sm font-medium ${sentimentColors[signal.sentiment]}`}>
            {signal.sentiment === 'bullish' ? '↑' : signal.sentiment === 'bearish' ? '↓' : '→'}
          </span>
        )}
      </div>

      <div className="flex items-center justify-between mt-3 text-xs text-star-dim">
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded ${config.color}`}>
            {signal.signal_type.replace('_', ' ')}
          </span>
          {signal.signal_subtype && (
            <span className="text-star-dim">• {signal.signal_subtype}</span>
          )}
        </div>
        <span>{formatDate(signal.signal_date || signal.created_at)}</span>
      </div>

      {signal.source_url && (
        <a
          href={signal.source_url}
          target="_blank"
          rel="noopener noreferrer"
          className="block mt-2 text-xs text-signal-blue hover:underline truncate"
        >
          {signal.source_url}
        </a>
      )}
    </div>
  )
}

// Run Status Badge
function RunStatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    pending: 'bg-yellow-500/20 text-yellow-400',
    researching: 'bg-blue-500/20 text-blue-400',
    normalizing: 'bg-purple-500/20 text-purple-400',
    synthesizing: 'bg-cyan-500/20 text-cyan-400',
    completed: 'bg-green-500/20 text-green-400',
    failed: 'bg-red-500/20 text-red-400',
  }

  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[status] || 'bg-gray-500/20 text-gray-400'}`}>
      {status}
    </span>
  )
}

// Progress Bar Component
function ProgressBar({ progress }: { progress: ResearchProgress }) {
  return (
    <div className="glass-card p-4 rounded-lg border border-signal-blue/30">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="animate-spin text-signal-blue">⟳</span>
          <span className="font-medium text-star-bright">Research in Progress</span>
        </div>
        <span className="text-sm text-star-dim">{progress.progress_pct}%</span>
      </div>
      <div className="h-2 bg-void-200/30 rounded-full overflow-hidden mb-2">
        <motion.div
          className="h-full bg-signal-blue"
          initial={{ width: 0 }}
          animate={{ width: `${progress.progress_pct}%` }}
          transition={{ duration: 0.3 }}
        />
      </div>
      <div className="flex items-center justify-between text-xs text-star-dim">
        <span>{progress.message}</span>
        <span>{progress.tasks_completed}/{progress.tasks_total} tasks • {progress.searches_completed} searches</span>
      </div>
      {progress.error && (
        <div className="mt-2 text-xs text-signal-red bg-signal-red/10 px-2 py-1 rounded">
          {progress.error}
        </div>
      )}
    </div>
  )
}

// Main Component
export function CompanyDetailPage() {
  const { companyId } = useParams<{ companyId: string }>()
  const [activeTab, setActiveTab] = useState<'overview' | 'signals' | 'runs'>('overview')
  const [signalTypeFilter, setSignalTypeFilter] = useState<string | undefined>()
  const [activeRunId, setActiveRunId] = useState<number | null>(null)
  const [progress, setProgress] = useState<ResearchProgress | null>(null)
  const queryClient = useQueryClient()

  const { data: company, isLoading: companyLoading } = useQuery({
    queryKey: ['company', companyId],
    queryFn: () => fetchCompany(companyId!),
    enabled: !!companyId,
  })

  const { data: signals = [] } = useQuery({
    queryKey: ['signals', companyId, signalTypeFilter],
    queryFn: () => fetchSignals(companyId!, signalTypeFilter),
    enabled: !!companyId,
  })

  const { data: report } = useQuery({
    queryKey: ['report', companyId],
    queryFn: () => fetchLatestReport(companyId!),
    enabled: !!companyId,
  })

  const { data: runs = [] } = useQuery({
    queryKey: ['runs', companyId],
    queryFn: () => fetchRuns(companyId!),
    enabled: !!companyId,
  })

  // Poll for progress of active research run
  const pollProgress = useCallback(async (runId: number) => {
    try {
      const prog = await fetchProgress(runId)
      setProgress(prog)

      // If completed or failed, stop polling and refresh data
      if (prog.status === 'completed' || prog.status === 'failed') {
        setActiveRunId(null)
        queryClient.invalidateQueries({ queryKey: ['company', companyId] })
        queryClient.invalidateQueries({ queryKey: ['runs', companyId] })
        queryClient.invalidateQueries({ queryKey: ['signals', companyId] })
        queryClient.invalidateQueries({ queryKey: ['report', companyId] })

        // Clear progress after a delay so user can see final state
        if (prog.status === 'completed') {
          setTimeout(() => setProgress(null), 3000)
        }
      }
    } catch (err) {
      console.error('Failed to poll progress:', err)
    }
  }, [companyId, queryClient])

  // Set up polling interval for active research
  useEffect(() => {
    if (!activeRunId) return

    const intervalId = setInterval(() => {
      pollProgress(activeRunId)
    }, 2000) // Poll every 2 seconds

    // Initial poll
    pollProgress(activeRunId)

    return () => clearInterval(intervalId)
  }, [activeRunId, pollProgress])

  const researchMutation = useMutation({
    mutationFn: () => triggerResearch(parseInt(companyId!)),
    onSuccess: (data) => {
      // Track this research run
      setActiveRunId(data.id)
      // Initialize progress
      setProgress({
        run_id: data.id,
        status: 'pending',
        progress_pct: 0,
        message: 'Starting research...',
        tasks_completed: 0,
        tasks_total: 7,
        searches_completed: 0,
      })
      queryClient.invalidateQueries({ queryKey: ['runs', companyId] })
    },
  })

  if (companyLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-signal-blue border-t-transparent rounded-full" />
      </div>
    )
  }

  if (!company) {
    return (
      <div className="glass-card p-6 rounded-lg">
        <p className="text-star-dim">Company not found</p>
        <Link to="/research" className="text-signal-blue hover:underline mt-2 block">
          Back to companies
        </Link>
      </div>
    )
  }

  const signalTypes = [...new Set(signals.map(s => s.signal_type))]

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Link to="/research" className="text-star-dim hover:text-star-bright">
              ← Back
            </Link>
          </div>
          <h1 className="text-3xl font-bold text-star-bright flex items-center gap-3">
            {company.ticker}
            {company.sector && (
              <span className="text-sm font-normal text-star-dim px-2 py-0.5 bg-void-200/20 rounded">
                {company.sector}
              </span>
            )}
          </h1>
          <p className="text-star-dim mt-1">{company.company_name}</p>
        </div>

        <button
          onClick={() => researchMutation.mutate()}
          disabled={researchMutation.isPending || activeRunId !== null}
          className="px-4 py-2 bg-signal-blue text-white rounded-lg hover:bg-signal-blue/80 transition-colors disabled:opacity-50"
        >
          {researchMutation.isPending ? 'Starting...' : activeRunId ? 'Research Running...' : 'Run Research'}
        </button>
      </div>

      {/* Progress Bar (when research is running) */}
      {progress && progress.status !== 'completed' && (
        <ProgressBar progress={progress} />
      )}

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="glass-card p-4 rounded-lg text-center">
          <div className="text-2xl font-bold text-star-bright">{company.total_signals}</div>
          <div className="text-sm text-star-dim">Signals</div>
        </div>
        <div className="glass-card p-4 rounded-lg text-center">
          <div className="text-2xl font-bold text-star-bright">{company.total_runs}</div>
          <div className="text-sm text-star-dim">Research Runs</div>
        </div>
        <div className="glass-card p-4 rounded-lg text-center">
          <div className="text-2xl font-bold text-star-bright">{company.total_reports}</div>
          <div className="text-sm text-star-dim">Reports</div>
        </div>
        <div className="glass-card p-4 rounded-lg text-center">
          <div className="text-sm text-star-bright">{formatDate(company.last_research_at)}</div>
          <div className="text-sm text-star-dim">Last Research</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b border-void-200/20 pb-2">
        {(['overview', 'signals', 'runs'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === tab
                ? 'text-signal-blue border-b-2 border-signal-blue'
                : 'text-star-dim hover:text-star-bright'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Executive Summary */}
          {report && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card p-6 rounded-lg"
            >
              <h2 className="text-xl font-semibold text-star-bright mb-4">Executive Summary</h2>
              <p className="text-star-dim whitespace-pre-wrap">{report.executive_summary}</p>
              <p className="text-xs text-star-dim mt-4">
                Generated: {formatDate(report.generated_at)}
              </p>
            </motion.div>
          )}

          {/* Scenarios */}
          {report?.scenarios && (
            <div>
              <h2 className="text-lg sm:text-xl font-semibold text-star-bright mb-3 sm:mb-4">Scenarios</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                <ScenarioCard name="bull" scenario={report.scenarios.bull} />
                <ScenarioCard name="base" scenario={report.scenarios.base} />
                <ScenarioCard name="bear" scenario={report.scenarios.bear} />
              </div>
            </div>
          )}

          {/* Key Monitors */}
          {report?.key_monitors && report.key_monitors.length > 0 && (
            <div className="glass-card p-4 rounded-lg">
              <h3 className="font-semibold text-star-bright mb-3">What Would Change Our View</h3>
              <ul className="space-y-2">
                {report.key_monitors.map((monitor, i) => (
                  <li key={i} className="flex items-start gap-2 text-star-dim">
                    <span className="text-signal-blue">→</span>
                    <span>{monitor}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Signals Dashboard */}
          {report?.signals_dashboard && Object.keys(report.signals_dashboard).length > 0 && (
            <div>
              <h2 className="text-xl font-semibold text-star-bright mb-4">Signals Dashboard</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(report.signals_dashboard).map(([type, data]) => {
                  const config = signalTypeConfig[type] || signalTypeConfig.news
                  return (
                    <div
                      key={type}
                      className={`glass-card p-4 rounded-lg border ${config.color}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span>{config.icon}</span>
                        <span className="font-medium capitalize">{type.replace('_', ' ')}</span>
                      </div>
                      <div className="text-2xl font-bold">{data.count}</div>
                      <div className={`text-sm ${
                        data.trend === 'bullish' ? 'text-green-400' :
                        data.trend === 'bearish' ? 'text-red-400' : 'text-star-dim'
                      }`}>
                        {data.trend}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {!report && (
            <div className="glass-card p-6 rounded-lg text-center">
              <p className="text-star-dim">No research report available</p>
              <button
                onClick={() => researchMutation.mutate()}
                className="mt-4 text-signal-blue hover:underline"
              >
                Run research to generate a report
              </button>
            </div>
          )}
        </div>
      )}

      {activeTab === 'signals' && (
        <div className="space-y-4">
          {/* Signal Type Filter */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSignalTypeFilter(undefined)}
              className={`px-3 py-1 rounded text-sm ${
                !signalTypeFilter
                  ? 'bg-signal-blue text-white'
                  : 'bg-void-200/20 text-star-dim hover:bg-void-200/30'
              }`}
            >
              All
            </button>
            {signalTypes.map((type) => (
              <button
                key={type}
                onClick={() => setSignalTypeFilter(type)}
                className={`px-3 py-1 rounded text-sm ${
                  signalTypeFilter === type
                    ? 'bg-signal-blue text-white'
                    : 'bg-void-200/20 text-star-dim hover:bg-void-200/30'
                }`}
              >
                {type.replace('_', ' ')}
              </button>
            ))}
          </div>

          {/* Signals List */}
          <div className="space-y-4">
            {signals.map((signal) => (
              <SignalCard key={signal.id} signal={signal} />
            ))}
          </div>

          {signals.length === 0 && (
            <div className="glass-card p-6 rounded-lg text-center">
              <p className="text-star-dim">No signals found</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'runs' && (
        <div className="space-y-4">
          {runs.map((run) => (
            <div key={run.id} className="glass-card p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-star-bright">Run #{run.id}</span>
                    <RunStatusBadge status={run.status} />
                  </div>
                  <p className="text-sm text-star-dim mt-1">
                    Started: {formatDate(run.started_at)}
                    {run.ended_at && ` • Ended: ${formatDate(run.ended_at)}`}
                  </p>
                </div>
                <div className="text-right text-sm text-star-dim">
                  <p>{run.exa_research_tasks_count} research tasks</p>
                  <p>{run.exa_search_queries_count} search queries</p>
                </div>
              </div>

              {run.error && (
                <p className="mt-2 text-sm text-signal-red">{run.error}</p>
              )}

              {run.tasks && run.tasks.length > 0 && (
                <div className="mt-3 pt-3 border-t border-void-200/20">
                  <p className="text-xs text-star-dim mb-2">Tasks:</p>
                  <div className="flex flex-wrap gap-2">
                    {run.tasks.map((task) => (
                      <span
                        key={task.id}
                        className={`px-2 py-0.5 rounded text-xs ${
                          task.status === 'completed'
                            ? 'bg-green-500/20 text-green-400'
                            : task.status === 'failed'
                            ? 'bg-red-500/20 text-red-400'
                            : 'bg-yellow-500/20 text-yellow-400'
                        }`}
                      >
                        {task.task_type}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}

          {runs.length === 0 && (
            <div className="glass-card p-6 rounded-lg text-center">
              <p className="text-star-dim">No research runs yet</p>
              <button
                onClick={() => researchMutation.mutate()}
                className="mt-4 text-signal-blue hover:underline"
              >
                Start your first research run
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
